import axios from 'axios';
import type { AxiosInstance, AxiosRequestConfig, AxiosError } from 'axios';
import { message, Modal } from 'ant-design-vue';
import NProgress from 'nprogress';
import { getLoginUrl, getToken } from '@/utils';
import { router } from '@/router';

let isApiDisabled = false;

export function setApiDisabled(status: boolean) {
  isApiDisabled = status;
}

interface ApiError {
  code: number;
  message: string;
}

declare module 'axios' {
  interface AxiosRequestConfig {
    noMessage?: boolean;
    noToken?: boolean;
    returnFullResponse?: boolean;
  }

  interface AxiosInstance {
    (config: AxiosRequestConfig): AxiosPromise<any>;
  }
}

class ApiClient {
  private axiosInstance: AxiosInstance;

  constructor() {
    this.axiosInstance = axios.create({
      timeout: 10000,
      baseURL: import.meta.env.VITE_API_URL
    });

    this.axiosInstance.interceptors.request.use((request) => {
      if (isApiDisabled) {
        return Promise.reject( 'API calls are disabled' );
      }
      NProgress.start();
      if (!request.noToken) {
        request.headers['Authorization'] = `Bearer ${getToken()}`;
      }
      return request;
    });

    this.axiosInstance.interceptors.response.use((response) => {
      NProgress.done();
      return response.config.returnFullResponse ? response : response.data || response;
    }, this.handleError);
  }

  private handleError(error: AxiosError<ApiError>): Promise<ApiError> | ApiError {
    NProgress.done();
    if (error.response) {
      const { status, data } = error.response;
      // 响应已接收，但状态码不在 2xx 范围内
      const path = window.location.hash.replace('#', '');
      if ([401].includes(status)
          && ['/login', '/reset-pass', '/create-account', '/account-creation', '/user-registration'].every(keyword => !path.includes(keyword))) {
        localStorage.clear();
        router.push('/login');
      } else if (error.response.data.message && !error.config?.noMessage) {
        message.error(error.response.data.message);
      }
      return error.response.config.returnFullResponse
        ? Promise.reject(error.response)
        : Promise.reject(data);
    } else if (error.request) {
      // 请求已发送但未收到响应
      return Promise.reject({
        code: 'network_error',
        message: 'ネットワークリクエストに失敗しました。ネットワーク接続を確認してください。'
      });
    } else {
      // 其他错误
      return Promise.reject({
        code: 'unknown_error',
        message: '不明なエラーが発生しました。もう一度お試しください。'
      });
    }
  }

  public get<T>(url: string, config?: AxiosRequestConfig) {
    return this.axiosInstance.get<T, T>(url, config);
  }

  public post<T>(url: string, data?: any, config?: AxiosRequestConfig) {
    return this.axiosInstance.post<T, T>(url, data, config);
  }

  public put<T>(url: string, data?: any, config?: AxiosRequestConfig) {
    return this.axiosInstance.put<T, T>(url, data, config);
  }

  public delete<T>(url: string, config?: AxiosRequestConfig) {
    return this.axiosInstance.delete<T, T>(url, config);
  }

  public patch<T>(url: string, data?: any, config?: AxiosRequestConfig) {
    return this.axiosInstance.patch<T, T>(url, data, config);
  }
}

export default new ApiClient();
